import DOMPurify from "dompurify";

const SANITIZE_CONFIG = { ADD_ATTR: ["colwidth", "colspan", "rowspan"] };

export const sanitizeHtml = (html: string) => DOMPurify.sanitize(html, SANITIZE_CONFIG);

/**
 * Hrefs that already do something other than load a page. Forcing a new tab on these gets you a
 * blank one that immediately hands off (mailto, tel) or a second copy of the whole app (#fragment).
 */
const HANDLED_WITHOUT_A_NAVIGATION = /^(?:mailto:|tel:|#)/i;

/**
 * Its own instance: the hook below must not reach `sanitizeHtml`, which also runs over draft bodies
 * on their way out. target/rel belong in what we render, not in the mail we send.
 */
const messagePurifier = DOMPurify();

/**
 * A link in a message body sits inside the app, so following it in place unmounts the SPA and loses
 * the reader's spot in the thread. Every mail client opens these in a new tab; so do we. Setting
 * the attribute rather than intercepting the click keeps middle-click, cmd-click and the context
 * menu behaving natively.
 *
 * Guarded because `@qw/ui` is also imported during server rendering (Attribution's Next.js app),
 * where DOMPurify has no window and returns an unsupported instance without `addHook`.
 */
if (messagePurifier.isSupported) {
  messagePurifier.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") {
      return;
    }

    const href = node.getAttribute("href");

    if (!href || HANDLED_WITHOUT_A_NAVIGATION.test(href)) {
      return;
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

export const sanitizeMessageHtml = (html: string) => messagePurifier.sanitize(html, SANITIZE_CONFIG);
